import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { SquareButton } from "../components/common";
import { useLocation, useNavigate } from "react-router-dom";
import noticeManager from "../apis/notice";
import { closeConfirm, openConfirm } from "../redux/ConfirmSlice";
import { useDispatch } from "react-redux";

const Notice = () => {
    const [title, setTitle] = useState("");
    const [contents, setContents] = useState("");
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState("");
    const fileInputRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { row, mode } = location.state || {};

    useEffect(() => {
        if (row && mode === "update") {
            const fileName = row.file_url?.replace("public/images/notice/", "");
            setTitle(row.title);
            setContents(row.contents);
            setFileName(fileName);
        }
    }, []);

    const handleUploaderClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e) => {
        setFile(e.target.files[0]);
        setFileName(e.target.files[0].name);
    };

    const handleSave = async () => {
        if (title === "") {
            dispatch(
                openConfirm({
                    confirmText: "확인",
                    text: "제목을 입력해 주세요.",
                    isSingleButton: true,
                    confirmFunc: () => {
                        dispatch(closeConfirm());
                    },
                })
            );
        } else if (contents === "") {
            dispatch(
                openConfirm({
                    confirmText: "확인",
                    text: "내용을 입력해 주세요.",
                    isSingleButton: true,
                    confirmFunc: () => {
                        dispatch(closeConfirm());
                    },
                })
            );
        } else {
            if (file instanceof Blob) {
                const formData = new FormData();
                // formData.append("image", file);
                const isPdf = file.type === "application/pdf";
                formData.append(isPdf ? "pdf" : "image", file);
                formData.append("title", title);
                formData.append("contents", contents);
                if (row && mode === "update") {
                    formData.append("id", row.id);
                    formData.append("file_url", row.file_url);
                    const res = await noticeManager.updateImage(formData);
                    const image = res.data;
                    dispatch(
                        openConfirm({
                            confirmText: "확인",
                            text: "공지사항이 수정되었습니다.",
                            isSingleButton: true,
                            confirmFunc: () => {
                                dispatch(closeConfirm());
                                navigate(`/notice-detail/${image.id}`, {
                                    state: { row: res, page: 1 },
                                });
                            },
                        })
                    );
                } else {
                    await noticeManager.uploadImage(formData);
                    dispatch(
                        openConfirm({
                            confirmText: "확인",
                            text: "공지사항이 작성되었습니다.",
                            isSingleButton: true,
                            confirmFunc: () => {
                                dispatch(closeConfirm());
                                navigate(`/notice-list`);
                            },
                        })
                    );
                }
            } else {
                const noticeInfo = {
                    title: title,
                    contents: contents,
                };
                if (row && mode === "update") {
                    noticeInfo.id = row.id;
                    const res = await noticeManager.updateNotice(noticeInfo);
                    const notice = res.data;
                    dispatch(
                        openConfirm({
                            confirmText: "확인",
                            text: "공지사항이 수정되었습니다.",
                            isSingleButton: true,
                            confirmFunc: () => {
                                dispatch(closeConfirm());
                                navigate(`/notice-detail/${notice.id}`, {
                                    state: { row: res, page: 1 },
                                });
                            },
                        })
                    );
                } else {
                    await noticeManager.createNotice(noticeInfo);
                    dispatch(
                        openConfirm({
                            confirmText: "확인",
                            text: "공지사항이 작성되었습니다.",
                            isSingleButton: true,
                            confirmFunc: () => {
                                dispatch(closeConfirm());
                                navigate(`/notice-list`);
                            },
                        })
                    );
                }
            }
        }
    };

    const goToListPage = () => {
        navigate("/notice-list");
    };

    return (
        <NoticePageContainer>
            <NoticeTitle>공지사항</NoticeTitle>
            <NoticeBodyContainer>
                <TableContainer>
                    <tbody>
                        <TableTitleArea>
                            <TableTitle>제목</TableTitle>
                            <TableTitleData>
                                <TableInput
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                />
                            </TableTitleData>
                        </TableTitleArea>
                        <TableContentsArea>
                            <TableContents style={{ verticalAlign: "top" }}>
                                내용
                            </TableContents>
                            <TableContentsData>
                                <TableTextArea
                                    value={contents}
                                    onChange={(e) =>
                                        setContents(e.target.value)
                                    }
                                />
                            </TableContentsData>
                        </TableContentsArea>
                        <TableFileArea>
                            <TableFile>첨부파일</TableFile>
                            <TableFileUpload>
                                <FileUploadHidden
                                    type="file"
                                    name="image"
                                    className="payments-inquire-file"
                                    accept="image/*,application/pdf"
                                    ref={fileInputRef}
                                    onChange={(e) => handleFileChange(e)}
                                />
                                <FileUploader>
                                    <SquareButton
                                        size="lg"
                                        onClick={handleUploaderClick}
                                    >
                                        {mode === "update" && row.file_url
                                            ? "파일변경"
                                            : "파일등록"}
                                    </SquareButton>
                                    <FileNameArea>{fileName}</FileNameArea>
                                </FileUploader>
                            </TableFileUpload>
                        </TableFileArea>
                    </tbody>
                </TableContainer>
            </NoticeBodyContainer>
            <NoticeFooterContainer>
                <CancelBtn onClick={goToListPage}>목록</CancelBtn>
                <SaveBtn onClick={handleSave}>저장</SaveBtn>
            </NoticeFooterContainer>
        </NoticePageContainer>
    );
};

const NoticePageContainer = styled.div`
    margin-top: 15px;
    padding: 72px;
    width: 100vw;
    height: 100vh;
    min-width: 1390px;
    min-height: 500px;
`;

const NoticeTitle = styled.p`
    font-weight: 500;
    font-size: 24px;
    line-height: 32px;
    color: #282B39;
`;

const NoticeBodyContainer = styled.div`
    width: 80%;
    margin-top: 30px;
    height: 450px;
`;

const TableTitleArea = styled.tr`
    height: 50px;
    border-bottom: 1px solid #ccc;
`;

const TableContentsArea = styled.tr`
    height: 350px;
    border-bottom: 1px solid #ccc;
`;

const TableFileArea = styled.tr`
    height: 50px;
`;

const TableContainer = styled.table`
    width: 100%;
    height: 100%;
    border-bottom: 1px solid #ccc;
    border-top: 1px solid #ccc;
    //background-color: rgb(240, 240, 245);
`;

const TableTitle = styled.td`
    padding: 20px 0 20px 40px;
    width: 180px;
    max-height: 130px;
    font-weight: bold;
    background-color: rgb(240, 240, 245);
    //border-right: 1px solid #000000;
`;

const TableContents = styled.td`
    padding-top: 40px;
    width: 180px;
    max-height: 130px;
    padding-left: 40px;
    font-weight: bold;
    background-color: rgb(240, 240, 245);
    //border-right: 1px solid #000000;
`;

const TableFile = styled.td`
    padding: 20px 0 20px 40px;
    width: 180px;
    max-height: 130px;
    font-weight: bold;
    background-color: rgb(240, 240, 245);
    //border-right: 1px solid #000000;
`;

const TableTitleData = styled.td`
    padding: 20px 0 20px 40px;
`;

const TableContentsData = styled.td`
    padding-left: 40px;
`;

const TableInput = styled.input`
    width: 90%;
    height: 40px;
    padding-left: 10px;
    //outline-color: rgb(238, 238, 240);
    border: 1px solid rgb(238, 238, 240);
    outline: none;
`;

const TableTextArea = styled.textarea`
    width: 90%;
    height: 80%;
    padding: 10px;
    outline-color: rgb(238, 238, 240);
    border: 1px solid rgb(238, 238, 240);
`;

const TableFileUpload = styled.td`
    padding: 20px 0 20px 40px;
    height: 80px;
    margin-bottom: 10px;
`;

const FileUploadHidden = styled.input`
    display: none;
`;

const NoticeFooterContainer = styled.div`
    display: flex;
    width: 80%;
    justify-content: center;
    margin-top: 120px;
    gap: 10px;
`;

const CancelBtn = styled.button`
    transition: 0.2s ease-in-out;
    cursor: pointer;
    padding: 0px 56px;
    height: 48px;
    font-size: 16px;
    border: 1px solid var(--gray-black);
    background-color: #fff;
    border-radius: 50px;
    color: var(--gray-black);
    font-weight: 700;
`;

const SaveBtn = styled.button`
    transition: 0.2s ease-in-out;
    cursor: pointer;
    padding: 0px 56px;
    height: 48px;
    font-size: 16px;
    border: 1px solid var(--gray-black);
    background-color: var(--gray-black);
    border-radius: 50px;
    color: rgb(255, 255, 255);
    font-weight: 700;
`;

const FileUploader = styled.div`
    display: flex;
`;

const FileNameArea = styled.p`
    display: flex;
    justify-content: start;
    align-items: center;
    margin-left: 20px;
`;

export default Notice;
